import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { BasketRightColumn } from './BasketRightColumn';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import { Multiselect } from 'ui/Multiselect';
import { BasketReferenceSearch } from './BasketReferenceSearch';
import { Link } from 'react-router-dom';
import { SvgIcon } from 'ui/SvgIcon';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';
import { useBasketUsers, useTravelAgent, useBasket, useBasketInfinite, IBasketPageData } from './useBasketQueries';
import {
  EUserType,
  EShareType,
  ICompany,
  ISearchQuery,
  ITravelAgent,
  IUser,
  Lodging,
  makeBackendApi,
  MealPlanNames,
} from 'services/BackendApi';
import { useQueryClient } from '@tanstack/react-query';
import { BasketItem, eBasketItemRenderType } from './BasketItem';
import { IBasketBuildL4, IBasketUserResponseItem } from 'services/BackendApi/types/Basket';
import classNames from 'classnames';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { History } from 'history';
import { ERoutingPreviousPage } from 'utils/routingUtils';
import * as InventoryHeaderSelectors from 'store/modules/inventoryHeader/selectors';
import { isNil } from 'lodash-es';
import * as ActingOnBehalfOfSelectors from 'store/modules/actingOnBehalfOf/selectors';
import * as ActingOnBehalfOfActions from 'store/modules/actingOnBehalfOf/actions';
import { useRawUrlParams } from 'hooks/useRawUrlParams';
import * as BasketActions from 'store/modules/basket/actions';
import * as BookingBuilderActions from 'store/modules/bookingBuilder/actions';
import * as AgentsActions from 'store/modules/agents/actions';
import * as AgentsSelectors from 'store/modules/agents/selectors';
import * as FastSearchActions from 'store/modules/fastSearch/actions';
import qs from 'qs';
import * as HotelActions from 'store/modules/hotel/actions';
import { APP_ROUTING_PATHS } from 'config';
import PureCheckbox from 'pureUi/Checkbox';
import { CopyButtonMultiple } from './components/CopyButtonMultiple';
import { ShareLinkButton } from './components/ShareLinkButton';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { ControlledCurrencyConversionProvider } from 'context/CurrencyConversion/CurrencyConversionContext';
import { TCurrencyCode } from 'interfaces';
import { updateBookingGuestInformationAction } from 'store/modules/bookingBuilder';
import { useNotifications } from 'hooks/useNotifications';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';
import WhatsappLinkIcon from 'ui/Icons/components/Whatsapp.component';
import EmailLinkIcon from 'ui/Icons/components/EmailLink.component';
import LinkIcon from 'ui/Icons/components/Link.component';
import { getBookingConfirmationClipboardFormat, buildToBooking } from './components/CopyButtonMultiple';
import * as BookingBuilderSelectors from 'store/modules/bookingBuilder/selectors';
import * as HotelAccommodationProductSelectors from 'store/modules/hotelAccommodationProducts/selectors';
import { IHAPFormattedAccommodationProduct } from 'store/modules/hotelAccommodationProducts/types';
import ShareIcon from 'ui/Icons/share.component.svg';
import CopyIcon from 'ui/Icons/copy.component.svg';
import { useAvailableCurrencies } from 'hooks/useAvailableCurrencies';
import { convertPrice, convertPriceCents } from 'utils/currencyConversion';
import { getCurrencySymbol } from 'utils';

interface IBasketPageParams {
  buildUuids?: string[];
  actingOnBehalfOfUserUuid?: string;
  travelAgentUuid?: string;
}

const BASKET_OWNER_TYPE_TO_ABBR_MAP = {
  [EUserType.ADMIN]: 'ADM',
  [EUserType.SR]: 'RES',
  [EUserType.TA]: 'TA',
  [EUserType.FINANCE]: 'FIN',
  [EUserType.RL]: 'RL',
};

/**
 * Get the default TA UUID for a basket user.
 * Prefers the provided queryParamTaUuid if valid, otherwise selects the TA with isLatestBasket=true, or the first TA.
 */
const getDefaultTaUuid = (basketUser: IBasketUserResponseItem, queryParamTaUuid?: string | null): string | null => {
  if (!basketUser.travelAgents || basketUser.travelAgents.length === 0) {
    return null;
  }

  // If a query param TA UUID is provided and exists in the user's TAs, use it
  if (queryParamTaUuid && basketUser.travelAgents.some(ta => ta.uuid === queryParamTaUuid)) {
    return queryParamTaUuid;
  }

  // Otherwise, prefer the TA with isLatestBasket=true, or fall back to the first TA
  const indexOfTaWithLatestBasket = basketUser.travelAgents.findIndex(ta => ta.isLatestBasket === true);
  return basketUser.travelAgents[indexOfTaWithLatestBasket >= 0 ? indexOfTaWithLatestBasket : 0].uuid!;
};

/**
 * Reset basket selection and related state when switching basket owner/TA.
 */
const resetBasketSelection = (
  setSelectedBuildUuids: React.Dispatch<React.SetStateAction<string[]>>,
  setHasInitiallyAutoSelected: React.Dispatch<React.SetStateAction<boolean>>,
  dispatch: any
) => {
  setSelectedBuildUuids([]);
  setHasInitiallyAutoSelected(false);
  dispatch(BookingBuilderActions.clearBookingBuilderGuestDetailsAction());
};

export const BasketPage: React.FC = React.memo(() => {
  const dispatch = useDispatch();
  const history = useHistory<History.LocationState>();
  const { showErrorNotification, showSuccessNotification } = useNotifications();
  const queryClient = useQueryClient();

  const isInventoryHeaderOpen = useSelector(InventoryHeaderSelectors.isMenuOpenSelector);
  const isTa = useSelector(AuthSelectors.isTA);
  const loggedInUser = useSelector(AuthSelectors.getCurrentUser) as IUser;

  const params = useRawUrlParams<IBasketPageParams>();
  const {
    buildUuids: queryParamBuildUuids,
    travelAgentUuid: queryParamTaUuid,
    actingOnBehalfOfUserUuid: queryActingOnBehalfOfUserUuid,
  } = params;

  let actingOnBehalfOfUuid = useSelector(ActingOnBehalfOfSelectors.actingOnBehalfOfUuidSelector);
  let actingOnBehalfOfRole = useSelector(ActingOnBehalfOfSelectors.actingOnBehalfOfRoleSelector);
  let actingOnBehalfOfUser = useSelector(ActingOnBehalfOfSelectors.actingOnBehalfOfUserSelector);

  const { dynamicParameters } = useDynamicParameters();
  const { isMobile } = useResponsive();
  const { sortedCurrencies } = useAvailableCurrencies();
  const [activeSubDrawer, setActiveSubDrawer] = useState<'none' | 'book' | 'share' | 'copy'>('none');
  const [shareLinkUuid, setShareLinkUuid] = useState<string | null>(null);
  const [isShareLoading, setIsShareLoading] = useState(false);
  const [isShareResultDrawerOpen, setIsShareResultDrawerOpen] = useState(false);

  const paymentTerms = useSelector(BookingBuilderSelectors.bookingPaymentTermsSelector);
  const formattedAccommodationProducts: IHAPFormattedAccommodationProduct[] = useSelector(
    HotelAccommodationProductSelectors.getHotelAccommodationProductsSelector
  );

  useEffect(() => {
    // if acting on behalf of is null, but we have a logged in user, assume the acting on behalf of user is the logged in user.
    // But only on page load, as user might clear field on purpose
    if (isNil(actingOnBehalfOfUser) && !isNil(loggedInUser)) {
      dispatch(
        ActingOnBehalfOfActions.setActingOnBehalfOfUserAction({
          uuid: loggedInUser.uuid,
          role: loggedInUser.type as EUserType,
        })
      );
    }
  }, []);

  const [shouldShowLeaveAlert, setShouldShowLeaveAlert] = useState(false);
  const [redirectLocation, setRedirectLocation] = useState<string | null>(null);
  const [taCompany, setTaCompany] = useState<ICompany | null>(null);

  // Dynamic top offset for the right column to prevent footer overlap
  const [rightColumnTop, setRightColumnTop] = useState<number | undefined>(undefined);
  const rightColumnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const footer = document.getElementById('app-footer');
      const column = rightColumnRef.current;
      if (!footer || !column) return;

      const footerRect = footer.getBoundingClientRect();
      const naturalTop = isInventoryHeaderOpen ? 190 : 140;
      const columnHeight = column.offsetHeight;
      const gap = 20;

      // Would the column's bottom overlap the footer?
      const columnBottom = naturalTop + columnHeight;
      if (footerRect.top < columnBottom + gap) {
        // Push column up so its bottom is above the footer
        setRightColumnTop(footerRect.top - columnHeight - gap);
      } else {
        setRightColumnTop(undefined);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInventoryHeaderOpen]);

  const [selectedBuildUuids, setSelectedBuildUuids] = useState<string[]>(queryParamBuildUuids ?? []);

  // Close any sub-drawer when selection is cleared
  useEffect(() => {
    if (selectedBuildUuids.length === 0) {
      setActiveSubDrawer('none');
    }
  }, [selectedBuildUuids.length]);

  // Basket Reference ID: input value (type-ahead) and applied value (drives search only on selection)
  const [basketReferenceInput, setBasketReferenceInput] = useState('');
  const [appliedBasketReferenceId, setAppliedBasketReferenceId] = useState('');

  const [selectedTaUserUuid, setSelectedTaUserUuid] = useState<string | null>(queryParamTaUuid ?? null);

  // React Query hooks for data fetching
  const basketUsersQuery = useBasketUsers(isTa);
  const basketUsers = useMemo(() => basketUsersQuery.data ?? [], [basketUsersQuery.data]);
  const travelAgentQuery = useTravelAgent(selectedTaUserUuid);

  const isMode2Active = !!appliedBasketReferenceId?.trim();

  // Mode 1: Infinite query for filter-based browsing with "Load More" pagination
  const basketInfiniteQuery = useBasketInfinite({
    travelAgentUuid: selectedTaUserUuid,
    actingOnBehalfOfUserUuid: actingOnBehalfOfUuid,
    isTa,
    actingOnBehalfOfUser,
    enabled: !isMode2Active,
  });

  // Mode 2: Single query for reference ID search (no pagination)
  const basketReferenceQuery = useBasket({
    humanReadableId: appliedBasketReferenceId?.trim() || undefined,
    travelAgentUuid: selectedTaUserUuid,
    actingOnBehalfOfUserUuid: actingOnBehalfOfUuid,
    isTa,
    actingOnBehalfOfUser,
    enabled: isMode2Active,
  });

  // Unified accessor: whichever mode is active
  const basketQuery = isMode2Active ? basketReferenceQuery : basketInfiniteQuery;

  // Derive builds and totalBuilds from the active query (single source of truth)
  const builds: IBasketBuildL4[] = useMemo(() => {
    if (isMode2Active) {
      return basketReferenceQuery.data?.builds ?? [];
    }
    return basketInfiniteQuery.data?.pages?.flatMap(p => p.builds) ?? [];
  }, [isMode2Active, basketReferenceQuery.data, basketInfiniteQuery.data]);

  const totalBuilds = useMemo(() => {
    if (isMode2Active) {
      return basketReferenceQuery.data?.total ?? 0;
    }
    return basketInfiniteQuery.data?.pages?.[0]?.total ?? 0;
  }, [isMode2Active, basketReferenceQuery.data, basketInfiniteQuery.data]);

  const selectedUniqueBasketBuild =
    selectedBuildUuids.length === 1 ? builds.find(build => build.uuid === selectedBuildUuids[0]) ?? null : null;

  const selectedBuildsForRightColumn: IBasketBuildL4[] = React.useMemo(
    () => builds.filter(b => selectedBuildUuids.includes(b.uuid)),
    [builds, selectedBuildUuids]
  );

  const isMultiBookingEnabled = !!dynamicParameters.ENABLE_MULTI_BASKET_BOOKING_TEMP;

  /**
   * Patch a single build's fields across all cached basket queries (both the infinite-query
   * pages and the Mode 2 single-query). Used for in-place updates that should NOT trigger
   * a refetch (e.g. commission, payment currency) — refetching causes the list to re-sort
   * by updated_at, which makes items "jump" in the UI.
   */
  const patchBuildInCache = useCallback(
    (buildUuid: string, patch: Partial<IBasketBuildL4>) => {
      const mapBuilds = (bs: IBasketBuildL4[]) => bs.map(b => (b.uuid === buildUuid ? { ...b, ...patch } : b));

      // Mode 1: infinite query — update each page
      queryClient.setQueriesData<{ pages: IBasketPageData[]; pageParams: unknown[] } | undefined>(
        { queryKey: ['basket', 'byFilters'] },
        oldData => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map(page => ({ ...page, builds: mapBuilds(page.builds) })),
          };
        }
      );

      // Mode 2: single query — update builds directly
      queryClient.setQueriesData<IBasketPageData | undefined>({ queryKey: ['basket', 'byReferenceId'] }, oldData => {
        if (!oldData?.builds) return oldData;
        return { ...oldData, builds: mapBuilds(oldData.builds) };
      });
    },
    [queryClient]
  );

  // Get TA and company data from Redux for edit navigation
  const selectedTa = useSelector(AgentsSelectors.selectedTaSelector);
  const selectedCompany = useSelector(AgentsSelectors.selectedCompanySelector);
  const selectedCompanyMembership = useSelector(AgentsSelectors.selectedCompanyMembershipSelector);

  const selectedBasketUser = isNil(actingOnBehalfOfUuid)
    ? null
    : basketUsers.find(user => user.basketOwner.uuid === actingOnBehalfOfUuid);

  const isBasketOwnerDropdownDisabled = basketUsersQuery.isLoading || basketQuery.isLoading || basketQuery.isFetching;

  const isTaDropdownDisabled =
    isBasketOwnerDropdownDisabled || actingOnBehalfOfRole === EUserType.TA || travelAgentQuery.isLoading;

  const sortAlphabetically = (array, field) => {
    return [...array].sort((a, b) => a[field].localeCompare(b[field]));
  };

  const [hasInitiallyAutoSelected, setHasInitiallyAutoSelected] = useState(false);

  const autoSelectFirstBuild = (builds: IBasketBuildL4[]) => {
    if (!hasInitiallyAutoSelected && builds.length > 0 && selectedBuildUuids.length === 0) {
      const firstValidBuild = builds.find(b => b.latestBookingBuilderResponse?.canBeBooked);
      if (firstValidBuild) {
        handleSetSelectedBuildUuids([firstValidBuild.uuid]);
        setHasInitiallyAutoSelected(true);
      }
    }
  };

  // Handle redirect when redirectLocation state changes
  useEffect(() => {
    if (redirectLocation) {
      history.push(redirectLocation, { comingFrom: ERoutingPreviousPage.BASKET_PAGE });
    }
  }, [redirectLocation, history]);

  // Handle basket users data and set up initial state when data loads
  useEffect(() => {
    if (isTa) {
      // if we're a TA, we're always acting on behalf of ourselves
      dispatch(
        ActingOnBehalfOfActions.setActingOnBehalfOfUserAction({
          uuid: loggedInUser.uuid,
          role: loggedInUser.type as EUserType,
        })
      );
      // Set selectedTaUserUuid to TA's own UUID so L4 enrichment runs
      setSelectedTaUserUuid(loggedInUser.uuid);
      return;
    }

    // Wait for basket users data to load
    if (!basketUsersQuery.data) {
      return;
    }

    // If we ARE setting an owner via URL query param, set it up
    if (queryActingOnBehalfOfUserUuid) {
      const basketUser = basketUsersQuery.data.find(user => user.basketOwner.uuid === queryActingOnBehalfOfUserUuid);
      if (basketUser) {
        dispatch(
          ActingOnBehalfOfActions.setActingOnBehalfOfUserAction({
            uuid: queryActingOnBehalfOfUserUuid,
            role: basketUser.basketOwner.type as EUserType,
          })
        );
        // Auto-populate TA from query param or default when coming from URL
        const taUuid = getDefaultTaUuid(basketUser, queryParamTaUuid);
        if (taUuid) {
          setSelectedTaUserUuid(taUuid);
        }
      }
    } else {
      // Initial page load: Set defaults for logged-in user (only if not already set)
      // This runs once when basketUsersQuery.data first loads, not when user manually changes dropdowns
      // if (isNil(actingOnBehalfOfUuid)) {
      const loggedInUserBasket = basketUsersQuery.data.find(user => user.basketOwner.uuid === loggedInUser.uuid);
      if (loggedInUserBasket) {
        dispatch(
          ActingOnBehalfOfActions.setActingOnBehalfOfUserAction({
            uuid: loggedInUser.uuid,
            role: loggedInUser.type as EUserType,
          })
        );
        // Auto-select default TA for initial load
        const taUuid = getDefaultTaUuid(loggedInUserBasket, queryParamTaUuid);
        if (taUuid) {
          setSelectedTaUserUuid(taUuid);
        }
      }
    }
    // }
  }, [isTa, basketUsersQuery.data]);

  // Handle travel agent data when it loads
  // a TA is selected then update the redux part for Agents
  // As commented with Tom, we are starting to have 2 concepts of TA
  // the TA from the BB as we used to have and now the "TA selected from dropdown basket"
  // Be aware that any of the 2 can be overwritten in that field of redux
  // This is the TA that will be used to req BE to create a booking and to show in <BasketTravelAgentInfo/>
  useEffect(() => {
    // if we ARE a TA, set the company
    if (isTa) {
      setTaCompany((loggedInUser as ITravelAgent).company!);
      return;
    }

    if (travelAgentQuery.data) {
      dispatch(AgentsActions.selectedTaChangeAction(travelAgentQuery.data));
      setTaCompany(travelAgentQuery.data.company!);
    }
  }, [isTa, travelAgentQuery.data]);

  // Mode 2: when a new reference ID is applied (new search), clear any prior selection.
  // We key this on appliedBasketReferenceId (not on query data) so that in-place cache
  // updates — e.g. a currency or commission patch on the already-selected build — don't
  // clear the selection underfoot.
  useEffect(() => {
    if (isMode2Active) {
      handleSetSelectedBuildUuids([]);
    }
  }, [appliedBasketReferenceId]);

  // Mode 2: auto-populate basket owner and TA dropdowns once the response arrives.
  // Safe to re-run on cache patches — setting the same AOBO/TA is effectively a no-op.
  useEffect(() => {
    if (!isMode2Active || !basketReferenceQuery.data) {
      return;
    }

    const { total, basketCreatorUuid, travelAgentUserUuid } = basketReferenceQuery.data;

    if (total === 0) {
      dispatch(ActingOnBehalfOfActions.setActingOnBehalfOfUserAction(null));
      setSelectedTaUserUuid(null);
      return;
    }

    if (basketCreatorUuid && travelAgentUserUuid) {
      const basketUser = basketUsers.find(user => user.basketOwner.uuid === basketCreatorUuid);
      if (basketUser) {
        dispatch(
          ActingOnBehalfOfActions.setActingOnBehalfOfUserAction({
            uuid: basketCreatorUuid,
            role: basketUser.basketOwner.type as EUserType,
          })
        );
      }
      setSelectedTaUserUuid(travelAgentUserUuid);
    }
  }, [basketReferenceQuery.data]);

  // Reconcile stale selection: remove UUIDs that no longer exist in builds
  useEffect(() => {
    if (builds.length === 0) return;
    setSelectedBuildUuids(prev => {
      const validUuids = prev.filter(uuid => builds.some(b => b.uuid === uuid));
      if (validUuids.length === prev.length) return prev;
      if (validUuids.length === 0) {
        setHasInitiallyAutoSelected(false);
      }
      return validUuids;
    });
  }, [builds]);

  // Auto-select first bookable build when builds change
  useEffect(() => {
    autoSelectFirstBuild(builds);
  }, [builds]);

  // Helper to set selected build UUIDs and clear guest info in one operation
  // This ensures guest details don't carry over when switching between different build selections
  const handleSetSelectedBuildUuids = (newValue: string[] | ((prev: string[]) => string[])) => {
    setSelectedBuildUuids(newValue);
    dispatch(
      updateBookingGuestInformationAction({
        guestTitle: null,
        guestFirstName: null,
        guestLastName: null,
        isRepeatGuest: false,
        comments: '',
      })
    );
  };

  const onEdit = (build: IBasketBuildL4) => {
    const bookingBuild = {
      request: build.buildRequest,
      response: build.latestBookingBuilderResponse!,
      bookingErrors: build.latestBookingBuilderResponse?.errors || [],
    };

    // the startDate and endDate below need to be the booking start and end dates
    const searchQuery: ISearchQuery = {
      name: null,
      lodgings: build.buildRequest.Accommodation.map(
        (accommodation): Lodging => ({
          numberOfAdults: accommodation.guestAges.numberOfAdults,
          agesOfAllChildren: accommodation.guestAges.agesOfAllChildren,
          repeatCustomer: accommodation.repeatCustomer,
        })
      ),
      startDate: build.buildRequest.startDate,
      endDate: build.buildRequest.endDate,
      mealPlanCategories: [MealPlanNames.ANY],
      regions: [],
      starRatings: [],
      filters: [],
      priceRange: {
        min: undefined,
        max: undefined,
      },
      clientCountryCode: !isTa ? build.clientCountryCode : null,
    };

    // Build complete query params including all necessary context
    const queryParams = {
      ...searchQuery,
      hotelUuid: build.buildRequest.hotelUuid,
      // Add TA/Company params for internal users to ensure proper initialization
      ...(selectedTa?.uuid && { tauuid: selectedTa.uuid }),
      ...(selectedCompany?.uuid && { companyUuid: selectedCompany.uuid }),
      ...(selectedCompanyMembership?.uuid && { membership: selectedCompanyMembership.uuid }),
    };

    const queryString = qs.stringify(queryParams);
    dispatch(HotelActions.clearHotelAction());
    dispatch(FastSearchActions.populateQueryAction(searchQuery));
    dispatch(BookingBuilderActions.setCurrentBookingBuilderAction(bookingBuild));
    dispatch(BasketActions.setBasketBuildAction(build));

    setRedirectLocation(`${APP_ROUTING_PATHS.basket.edit}?${queryString}`);
  };

  const handleHoldSuccess = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: ['basket'] });
  }, [queryClient]);

  // Mobile drawer: share handler
  const handleMobileShare = useCallback(
    async (shareType: EShareType) => {
      const backendApi = makeBackendApi();
      setIsShareLoading(true);
      try {
        const selectedBuilds = builds.filter(build => selectedBuildUuids.includes(build.uuid));
        const res = await backendApi.getShareLink(
          {
            travelAgentUuid: isTa ? undefined : selectedTaUserUuid ?? undefined,
            actingOnBehalfOfUserUuid: isTa ? undefined : actingOnBehalfOfUuid ?? undefined,
          },
          selectedBuilds.map(build => build.uuid),
          shareType
        );
        setIsShareLoading(false);
        setShareLinkUuid(res.data.link);
        setIsShareResultDrawerOpen(true);
      } catch (e) {
        setIsShareLoading(false);
        showErrorNotification('Error getting Share Link');
      }
    },
    [builds, selectedBuildUuids, isTa, selectedTaUserUuid, actingOnBehalfOfUuid, showErrorNotification]
  );

  // Mobile drawer: copy handler
  const handleMobileCopy = useCallback(
    async (mode: string) => {
      try {
        const selectedBuilds = builds.filter(build => selectedBuildUuids.includes(build.uuid));
        const clipboardContent = selectedBuilds
          .map(build => {
            const booking = buildToBooking(build);
            const taMarginAmount = parseFloat(build.taMarginAmount || '0');
            const hotelCurrency = build.initialBuildResponse.currency as TCurrencyCode;
            const paymentCurrency = build.paymentCurrency;
            const selectedCurrencyData = paymentCurrency
              ? sortedCurrencies.find(c => c.to.code === paymentCurrency) ?? null
              : null;

            return getBookingConfirmationClipboardFormat(booking, paymentTerms, formattedAccommodationProducts, {
              isFull: ['full-without-commission', 'full-with-commission'].includes(mode),
              withCommission: ['summary-with-commission', 'full-with-commission'].includes(mode),
              commissionPercentage: taMarginAmount,
              humanReadableId: build.humanReadableId,
              currencySymbolOverride: selectedCurrencyData
                ? getCurrencySymbol(paymentCurrency as TCurrencyCode)
                : undefined,
              convert: selectedCurrencyData
                ? (amount: number) => convertPrice(amount, hotelCurrency, selectedCurrencyData)
                : undefined,
              convertCents: selectedCurrencyData
                ? (amountCents: number) => convertPriceCents(amountCents, hotelCurrency, selectedCurrencyData)
                : undefined,
            });
          })
          .join('\n------------------\n\n');
        await navigator.clipboard.writeText(clipboardContent);
        showSuccessNotification('Copied booking information to clipboard');
        setActiveSubDrawer('none');
      } catch (e) {
        showErrorNotification('Failed to copy booking information to clipboard');
      }
    },
    [
      builds,
      selectedBuildUuids,
      paymentTerms,
      formattedAccommodationProducts,
      showSuccessNotification,
      showErrorNotification,
      sortedCurrencies,
    ]
  );

  const handleRemoveBuild = (basketBuildUuid?: string) => {
    if (basketBuildUuid) {
      handleSetSelectedBuildUuids(prev => prev.filter(uuid => uuid !== basketBuildUuid));
    }
    queryClient.invalidateQueries({ queryKey: ['basket'] });
  };

  return (
    <div className="basket-page mx-[20px] crm-desktop:mx-[80px] font-pt-sans text-black">
      <div className="basket-columns flex gap-[40px]">
        {/* the widths are the size of the right coluimn AND the x padding of the container */}
        <div
          className={classNames(
            'basket-left-column flex flex-col w-full md:w-[calc(100%-280px)] lg:w-[calc(100%-390px)]',
            {
              'opacity-50 pointer-events-none': !isMode2Active && basketInfiniteQuery.isFetchingNextPage,
            }
          )}
        >
          <div className="page-title-container flex items-center justify-between mb-20px">
            <h1 className={'primary-title font-noe-display text-4xl leading-46px text-black font-normal m-0'}>
              Basket
            </h1>
          </div>

          {/* the "user" dropdowns */}
          {!isTa && basketUsersQuery.isLoading && <LoadingBar label="Loading content..." className="mt-[20px]" />}
          {!isTa && basketUsersQuery.isError && <ErrorBar />}
          {!isTa && basketUsersQuery.isSuccess && (
            <div className="filters grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-20px">
              {/* Basket Reference ID Input */}
              <div className="flex flex-col space-y-2 w-full">
                <span className="uppercase text-xs text-gray-100 font-hurmegeometric-sans">Basket Reference ID</span>
                <BasketReferenceSearch
                  id="basket-reference-id"
                  value={basketReferenceInput}
                  onChange={setBasketReferenceInput}
                  onSelect={id => setAppliedBasketReferenceId(id || '')}
                  placeholder="BK-999AF-24-00001"
                  inputClassName="!font-hurmegeometric-sans text-sm !mt-0 !h-[35px] placeholder:text-gray-100"
                  disabled={isBasketOwnerDropdownDisabled}
                />
              </div>

              <div className="flex flex-col space-y-2 w-full">
                <span className="uppercase text-xs text-gray-100 font-hurmegeometric-sans">Basket Created by *</span>
                <span
                  className={classNames({
                    'opacity-50 pointer-events-none': isBasketOwnerDropdownDisabled,
                  })}
                >
                  <Multiselect
                    disabled={isBasketOwnerDropdownDisabled}
                    className="bg-ivory !font-hurmegeometric-sans"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-20"
                    itemContentClassName="!font-hurmegeometric-sans"
                    fontClass="!font-hurmegeometric-sans"
                    options={sortAlphabetically(
                      basketUsers.map(bu => {
                        return {
                          value: bu.basketOwner.uuid,
                          label: `${BASKET_OWNER_TYPE_TO_ABBR_MAP[bu.basketOwner.type]} - ${bu.basketOwner.firstName} ${
                            bu.basketOwner.lastName
                          }::${bu.basketOwner.type}`,
                        };
                      }),
                      'label'
                    )}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    isSingleSelectMode={true}
                    optionsLabelRenderer={o => o.label.split('::')[0]}
                    selectedValuesRenderer={v => {
                      const [label, role] = v.split('::');
                      return label;
                    }}
                    onUpdate={(sv, isCustom, options) => {
                      if (sv.length <= 0) {
                        // User is clearing the dropdown - allow empty state
                        dispatch(ActingOnBehalfOfActions.setActingOnBehalfOfUserAction(null));
                        setSelectedTaUserUuid(null);
                        setBasketReferenceInput('');
                        setAppliedBasketReferenceId('');
                        // Clear React Query cache to prevent stale data flash
                        queryClient.resetQueries({ queryKey: ['basket'] });
                        resetBasketSelection(setSelectedBuildUuids, setHasInitiallyAutoSelected, dispatch);
                      } else {
                        // Check if this is a no-op (same value selected again)
                        if (sv[0] === actingOnBehalfOfUuid) {
                          return; // Don't do anything if the value hasn't changed
                        }

                        setBasketReferenceInput('');
                        setAppliedBasketReferenceId('');
                        const user = options.find(o => o.value === sv[0])!;
                        const [, role] = user.label.split('::');

                        // IMPORTANT: Clear TA BEFORE updating acting on behalf to prevent race condition
                        // If we dispatch Redux action first, basketQuery briefly sees both conditions met and starts loading
                        setSelectedTaUserUuid(null);

                        // If we've just selected a TA as the basket owner, set the TA UUID to them
                        if (role === EUserType.TA) {
                          setSelectedTaUserUuid(sv[0]);
                        }

                        // Now update the acting on behalf user
                        dispatch(
                          ActingOnBehalfOfActions.setActingOnBehalfOfUserAction({
                            uuid: sv[0],
                            role,
                          })
                        );

                        // Clear React Query cache to prevent stale data flash
                        queryClient.resetQueries({ queryKey: ['basket'] });
                        resetBasketSelection(setSelectedBuildUuids, setHasInitiallyAutoSelected, dispatch);
                      }
                    }}
                    selectedValues={selectedBasketUser ? [selectedBasketUser.basketOwner.uuid] : []}
                    isEnableFuzzySearch={true}
                  />
                </span>
              </div>
              <div className="flex flex-col space-y-2 w-full">
                <span className="uppercase text-xs text-gray-100 font-hurmegeometric-sans">
                  Travel Company - Travel Agent *
                </span>
                <span
                  className={classNames({
                    'opacity-50 pointer-events-none': isTaDropdownDisabled,
                  })}
                >
                  <Multiselect
                    disabled={isTaDropdownDisabled}
                    className="bg-ivory !font-hurmegeometric-sans"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-20"
                    itemContentClassName="!font-hurmegeometric-sans"
                    fontClass="!font-hurmegeometric-sans"
                    options={sortAlphabetically(
                      selectedBasketUser
                        ? selectedBasketUser.travelAgents.map(ta => {
                            return {
                              value: ta.uuid,
                              label: `${ta.company.name} - ${ta.firstName} ${ta.lastName}`,
                            };
                          })
                        : [],
                      'label'
                    )}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    isSingleSelectMode={true}
                    onUpdate={sv => {
                      if (sv.length >= 1) {
                        // if they've actually selected a TA, clear the basket reference ID
                        setBasketReferenceInput('');
                        setAppliedBasketReferenceId('');
                        setSelectedTaUserUuid(sv[0]);

                        // Reset selection and autoSelection state when TA changes
                        // This ensures the first item of the new TA's basket is autoSelected
                        handleSetSelectedBuildUuids([]);
                        setHasInitiallyAutoSelected(false);
                      }
                      // Don't clear builds/offset here - basket owner dropdown already handles it
                      // Clearing again here causes double-clearing and timing issues with React Query
                    }}
                    selectedValues={selectedTaUserUuid ? [selectedTaUserUuid] : []}
                    isEnableFuzzySearch={true}
                  />
                </span>
              </div>
            </div>
          )}

          {/* Header + Actions */}
          {basketQuery.data && (
            <div className="page-title-container flex items-center justify-between">
              <div className="flex items-left items-center justify-between">
                <span className="ml-5">
                  <span>
                    <PureCheckbox
                      onChange={() => {
                        const totalSelectableCount = builds.filter(
                          build => build.latestBookingBuilderResponse?.canBeBooked
                        ).length;

                        if (selectedBuildUuids.length === totalSelectableCount && totalSelectableCount > 0) {
                          handleSetSelectedBuildUuids([]);
                          return;
                        }
                        handleSetSelectedBuildUuids([
                          ...builds
                            .filter(build => build.latestBookingBuilderResponse?.canBeBooked)
                            .map(build => build.uuid),
                        ]);
                      }}
                      checked={
                        builds.length > 0 &&
                        builds.some(build => build.latestBookingBuilderResponse?.canBeBooked) &&
                        selectedBuildUuids.length ===
                          builds.filter(build => build.latestBookingBuilderResponse?.canBeBooked).length
                      }
                      variant="modern"
                      className="checkbox w-[25px] h-[25px]"
                    />
                  </span>
                </span>
                <h1 className={'primary-title font-noe-display text-26px leading-33 text-black font-normal m-0 ml-5'}>
                  Select Product
                </h1>
              </div>
              <div className="hidden md:flex items-right justify-between">
                {dynamicParameters.ENABLE_SHARE_BASKET_TEMP && (
                  <ShareLinkButton
                    builds={builds.filter(build => selectedBuildUuids.includes(build.uuid))}
                    isTa={isTa}
                    selectedTaUserUuid={selectedTaUserUuid}
                    actingOnBehalfOfUuid={actingOnBehalfOfUuid}
                  />
                )}
                <CopyButtonMultiple builds={builds.filter(build => selectedBuildUuids.includes(build.uuid))} />
              </div>
            </div>
          )}

          {/* results but no results */}
          {(travelAgentQuery.isLoading || basketQuery.isLoading) && !basketUsersQuery.isLoading && (
            <LoadingBar label={'Loading content...'} className="mt-[20px]" />
          )}
          {basketQuery.isError && <ErrorBar />}
          {(basketQuery.isSuccess && builds.length <= 0 && (
            <div className="flex flex-col">
              <span className="font-hurmegeometric-sans mt-4 text-15px leading-18px mb-20px">No results found</span>

              <Link
                className="block self-start bg-white border border-brown-prime font-hurmegeometric-sans uppercase px-10px py-[9px] text-brown-prime text-sm"
                to="/filters"
              >
                <span className="text-brown-prime">Search Products</span>
              </Link>
            </div>
          )) ??
            null}

          {/* results */}
          <div
            className={classNames('results flex flex-col space-y-10px mt-20px', {
              'opacity-50 pointer-events-none': travelAgentQuery.isLoading || basketQuery.isLoading,
              'pb-[70px]': isMobile && selectedBuildUuids.length > 0,
            })}
          >
            {(basketQuery.isSuccess || (basketInfiniteQuery.isSuccess && basketInfiniteQuery.isFetching)) &&
              builds.map(build => (
                <ControlledCurrencyConversionProvider
                  key={build.uuid}
                  hotelCurrency={build.initialBuildResponse.currency as TCurrencyCode}
                  isEnabled={dynamicParameters.ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP === true}
                  initialCurrencyCode={build.paymentCurrency}
                >
                  <BasketItem
                    build={build}
                    isSelected={selectedBuildUuids.includes(build.uuid)}
                    onSelectBuild={(uuid, newState) => {
                      if (newState) {
                        handleSetSelectedBuildUuids(oldArray => [...oldArray, uuid]);
                      } else {
                        handleSetSelectedBuildUuids(oldArray => oldArray.filter(selectedBuid => selectedBuid !== uuid));
                      }
                    }}
                    selectionType="multiple"
                    onEdit={() => onEdit(build)}
                    onDeleteBuild={uuid => {
                      handleSetSelectedBuildUuids(oldArray => oldArray.filter(selectedBuid => selectedBuid !== uuid));
                      queryClient.invalidateQueries({ queryKey: ['basket'] });
                    }}
                    onUpdateBuildCommission={(buildUuid, taMarginAmount) => {
                      // In-place patch (no refetch) so the list doesn't re-sort and items don't jump
                      patchBuildInCache(buildUuid, { taMarginAmount });
                    }}
                    actingOnBehalfOfUser={actingOnBehalfOfUser || undefined}
                    selectedTaUserUuid={selectedTaUserUuid}
                    basketItemRenderType={eBasketItemRenderType.BASKET_PAGE}
                    isPaymentCurrencyEnabled={dynamicParameters.ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP === true}
                    onPaymentCurrencyUpdated={(buildUuid, newCurrency) => {
                      // In-place patch (no refetch) so the list doesn't re-sort and items don't jump
                      patchBuildInCache(buildUuid, { paymentCurrency: newCurrency });
                    }}
                  />
                </ControlledCurrencyConversionProvider>
              ))}
          </div>

          {/* the load more button */}
          {!isMode2Active && basketInfiniteQuery.hasNextPage && basketInfiniteQuery.isSuccess && (
            <button
              className="bg-white mt-20px self-center block border border-brown-prime font-hurmegeometric-sans uppercase px-10px py-[9px] text-brown-prime text-sm items-center space-x-5px cursor-pointer min-w-[125px]"
              disabled={basketInfiniteQuery.isFetchingNextPage}
              onClick={() => {
                basketInfiniteQuery.fetchNextPage();
              }}
            >
              {basketInfiniteQuery.isFetchingNextPage ? (
                <SvgIcon
                  IconComponent={LoaderIcon}
                  className="w-4 h-4 fill-transparent stroke-brown-140 stroke-2 animate-spin"
                />
              ) : (
                `Load More`
              )}
            </button>
          )}
        </div>

        <div
          ref={rightColumnRef}
          className={classNames(
            'basket-right-column-wrapper hidden md:block fixed right-[20px] crm-desktop:right-[80px] w-[260px] lg:w-[370px] transition-[top] duration-[60] overflow-x-clip',
            {
              // the odd heights are to ensure that we can always scroll down the right hand side and see everything
              // we need them because the right column is position:fixed
              'overflow-y-auto max-h-[calc(100dvh-200px)] top-[190px]': isInventoryHeaderOpen,
              'overflow-y-auto max-h-[calc(100dvh-150px)] top-[140px]': !isInventoryHeaderOpen,
            }
          )}
          style={rightColumnTop !== undefined ? { top: `${rightColumnTop}px` } : undefined}
        >
          {taCompany && (
            <ControlledCurrencyConversionProvider
              hotelCurrency={(selectedUniqueBasketBuild?.initialBuildResponse.currency || 'USD') as TCurrencyCode}
              isEnabled={dynamicParameters.ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP === true && !!selectedUniqueBasketBuild}
              initialCurrencyCode={selectedUniqueBasketBuild?.paymentCurrency}
              key={`${selectedUniqueBasketBuild?.uuid || 'no-selection'}-${selectedUniqueBasketBuild?.paymentCurrency ||
                'none'}`}
            >
              <BasketRightColumn
                basketBuild={selectedUniqueBasketBuild || null}
                selectedBuilds={selectedBuildsForRightColumn}
                shouldShowLeaveAlert={shouldShowLeaveAlert}
                setShouldShowLeaveAlert={setShouldShowLeaveAlert}
                setRedirectLocation={setRedirectLocation}
                onPostRemove={handleRemoveBuild}
                company={taCompany}
                clientCountryCode={selectedUniqueBasketBuild?.clientCountryCode || null}
                onHoldSuccess={handleHoldSuccess}
                isMultiBookingEnabled={isMultiBookingEnabled}
                selectedTaUserUuid={selectedTaUserUuid}
              />
            </ControlledCurrencyConversionProvider>
          )}
        </div>
      </div>

      {/* Mobile: Fixed bottom action bar */}
      {isMobile && taCompany && selectedBuildUuids.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-40 px-[20px] py-[12px] z-50 md:hidden">
          <div className="flex items-center gap-[10px]">
            {selectedBuildUuids.length === 1 && (
              <button
                className="flex-1 bg-brown-prime text-white font-hurmegeometric-sans uppercase text-sm py-[12px] cursor-pointer"
                onClick={() => setActiveSubDrawer('book')}
              >
                Book
              </button>
            )}
            {dynamicParameters.ENABLE_SHARE_BASKET_TEMP && (
              <button
                className="flex-1 bg-white border border-brown-prime text-brown-prime font-hurmegeometric-sans uppercase text-sm py-[12px] cursor-pointer flex items-center justify-center gap-[8px]"
                onClick={() => setActiveSubDrawer('share')}
              >
                <SvgIcon IconComponent={ShareIcon} width="16px" height="16px" className="fill-brown-prime" />
                Share ({selectedBuildUuids.length})
              </button>
            )}
            <button
              className="flex-1 bg-white border border-brown-prime text-brown-prime font-hurmegeometric-sans uppercase text-sm py-[12px] cursor-pointer flex items-center justify-center gap-[8px]"
              onClick={() => setActiveSubDrawer('copy')}
            >
              <SvgIcon IconComponent={CopyIcon} width="16px" height="16px" className="fill-brown-prime" />
              Copy ({selectedBuildUuids.length})
            </button>
          </div>
        </div>
      )}

      {/* Mobile: Book drawer */}
      {isMobile && taCompany && (
        <MobileBottomDrawer
          isOpen={activeSubDrawer === 'book'}
          onClose={() => setActiveSubDrawer('none')}
          title="Booking Details"
          stickyTitle={true}
        >
          <ControlledCurrencyConversionProvider
            hotelCurrency={(selectedUniqueBasketBuild?.initialBuildResponse.currency || 'USD') as TCurrencyCode}
            isEnabled={dynamicParameters.ENABLE_PAYMENT_CURRENCY_CHANGE_TEMP === true && !!selectedUniqueBasketBuild}
            initialCurrencyCode={selectedUniqueBasketBuild?.paymentCurrency}
            key={`mobile-${selectedUniqueBasketBuild?.uuid ||
              'no-selection'}-${selectedUniqueBasketBuild?.paymentCurrency || 'none'}`}
          >
            <BasketRightColumn
              basketBuild={selectedUniqueBasketBuild || null}
              selectedBuilds={selectedBuildsForRightColumn}
              shouldShowLeaveAlert={shouldShowLeaveAlert}
              setShouldShowLeaveAlert={setShouldShowLeaveAlert}
              setRedirectLocation={setRedirectLocation}
              onPostRemove={handleRemoveBuild}
              company={taCompany}
              clientCountryCode={selectedUniqueBasketBuild?.clientCountryCode || null}
              onHoldSuccess={handleHoldSuccess}
              isMultiBookingEnabled={isMultiBookingEnabled}
              selectedTaUserUuid={selectedTaUserUuid}
            />
          </ControlledCurrencyConversionProvider>
        </MobileBottomDrawer>
      )}

      {/* Mobile: Share drawer */}
      {isMobile && taCompany && (
        <MobileBottomDrawer
          isOpen={activeSubDrawer === 'share'}
          onClose={() => {
            setIsShareResultDrawerOpen(false);
            setActiveSubDrawer('none');
          }}
          title="Share"
        >
          <div className="flex flex-col gap-[10px]">
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileShare(EShareType.TA)}
            >
              To Travel Partner
            </button>
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileShare(EShareType.CLIENT)}
            >
              To Final Client
            </button>
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileShare(EShareType.CLIENT_ONLY_TOTAL)}
            >
              To Final Client (Only Total)
            </button>
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileShare(EShareType.CLIENT_NO_PRICES)}
            >
              To Final Client (No Prices)
            </button>
          </div>

          {/* Child drawer: Share result with link + actions */}
          <MobileBottomDrawer
            isOpen={isShareResultDrawerOpen}
            onClose={() => setIsShareResultDrawerOpen(false)}
            title="Share Items"
          >
            {isShareLoading ? (
              <div className="flex justify-center py-[20px]">
                <LoadingBar />
              </div>
            ) : (
              <div className="flex flex-col gap-[15px]">
                <label className="w-full">
                  <span className="block mb-[6px] text-gray-100 text-12px font-hurmegeometric-sans">URL</span>
                  <input
                    type="text"
                    className="p-[10px] text-gray-100 border border-gray-40 w-full text-14px font-hurmegeometric-sans"
                    value={shareLinkUuid ? `${window.location.origin}/shared/${shareLinkUuid}` : ''}
                    disabled
                    readOnly
                  />
                </label>
                <div className="flex gap-[8px]">
                  <button
                    className="flex-1 flex items-center justify-center gap-[5px] border border-brown-80 text-brown-80 bg-white py-[10px] font-hurmegeometric-sans text-13px uppercase cursor-pointer"
                    onClick={() => {
                      const shareLink = `${window.location.origin}/shared/${shareLinkUuid}`;
                      const body = `Thanks for your enquiry. Please find details of the options available together with prices and additional information below. If you would like to proceed or need further information please feel free to ask me any questions: ${shareLink}`;
                      window.open(`https://wa.me?text=${encodeURIComponent(body)}`, '_blank');
                    }}
                  >
                    <WhatsappLinkIcon />
                    <span>WhatsApp</span>
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-[5px] border border-brown-80 text-brown-80 bg-white py-[10px] font-hurmegeometric-sans text-13px uppercase cursor-pointer"
                    onClick={() => {
                      const shareLink = `${window.location.origin}/shared/${shareLinkUuid}`;
                      const body = `Thanks for your enquiry. Please find details of the options available together with prices and additional information below. If you would like to proceed or need further information please feel free to ask me any questions: ${shareLink}`;
                      const subject = 'Enquiry - Options, Availability and Costs.';
                      window.open(
                        `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
                        '_blank'
                      );
                    }}
                  >
                    <EmailLinkIcon />
                    <span>E-Mail</span>
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-[5px] border border-brown-80 text-brown-80 bg-white py-[10px] font-hurmegeometric-sans text-13px uppercase cursor-pointer"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${window.location.origin}/shared/${shareLinkUuid}`);
                        showSuccessNotification('Link copied successfully');
                      } catch (e) {
                        showErrorNotification('Failed to copy link');
                      }
                    }}
                  >
                    <LinkIcon />
                    <span>Copy Link</span>
                  </button>
                </div>
              </div>
            )}
          </MobileBottomDrawer>
        </MobileBottomDrawer>
      )}

      {/* Mobile: Copy drawer */}
      {isMobile && taCompany && (
        <MobileBottomDrawer isOpen={activeSubDrawer === 'copy'} onClose={() => setActiveSubDrawer('none')} title="Copy">
          <div className="flex flex-col gap-[10px]">
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileCopy('summary-without-commission')}
            >
              Summary (Travel Partner)
            </button>
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileCopy('summary-with-commission')}
            >
              Summary (Final Client)
            </button>
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileCopy('full-without-commission')}
            >
              Full (Travel Partner)
            </button>
            <button
              className="w-full bg-white border border-gray-40 text-black font-hurmegeometric-sans text-sm py-[12px] cursor-pointer text-left px-[16px]"
              onClick={() => handleMobileCopy('full-with-commission')}
            >
              Full (Final Client)
            </button>
          </div>
        </MobileBottomDrawer>
      )}

      <LeaveWithoutSavingModal
        title="You have made changes to this page, and not saved. If you leave this page now, these changes will be lost."
        when={shouldShowLeaveAlert}
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
      />
    </div>
  );
});
